// ============================================
// COMPANY DETAILS — SINGLE SOURCE OF TRUTH
// ============================================
// One Purpose's own company + bank details live in ONE place:
//   - Source of truth  = the `settings` table in Supabase
//     (editable ONLY via Settings → Company Profile, which requires
//      a passcode + a second super-admin approval).
//   - Fallback          = DEFAULT_COMPANY below (correct values).
//   - All PDFs (quotes, invoices, proformas, credit applications)
//     read from here — no more hardcoded copies per component.
// ============================================

// NOTE: This module must stay Node-safe (no window/localStorage at top
// level) so the local PDF render test can load it. In the browser, the
// global fetch wrapper (config.js) attaches auth automatically.
// Same env-aware URL as api.js / config.js (kept inline to avoid pulling
// config.js → window at import time, which breaks Node-based render tests).
const API_URL = process.env.NODE_ENV === 'production'
  ? 'https://onepurposelogistics-api.onrender.com/api'
  : 'http://localhost:5000/api';

// Correct current details (match the settings table seed). This is only
// used if the settings API is unreachable.
export const DEFAULT_COMPANY = {
  company_name: 'One Purpose Logistics',
  company_short_name: 'One Purpose Logistics',
  company_reg_number: '2023/124065/07',
  company_vat_number: '4260317641',
  company_phone: '+27 63 598 5855',
  company_email: 'info@onepurposelogistics.com',
  company_website: 'www.onepurposelogistics.com',
  company_address: '4 Wells Street',
  company_address_line2: 'Farramere, Gauteng, 1501',
  company_logo: '/Images/One Purpose Header Invoice.png',

  // Bank details
  bank_account_name: 'ONE PURPOSE LOGISTICS',
  bank_name: 'FNB',
  bank_account_number: '63230814104',
  bank_account_type: 'CHEQUE',
  bank_branch_code: '251655',
};

// ---- cache (5 minutes) ---------------------------------------------------
let cached = null;
let cachedAt = 0;
const CACHE_MS = 5 * 60 * 1000;

/**
 * Fetch company details from the settings table and merge over the defaults.
 * The settings GET returns `raw` (array of {category,key,value}) — we flatten
 * by key so settings override DEFAULT_COMPANY.
 */
export async function getCompanyDetails() {
  if (cached && Date.now() - cachedAt < CACHE_MS) return cached;

  // Portal logins (client / subbie) cannot read the full settings table — it is
  // staff-only — so they use the whitelisted branding endpoint instead. That
  // also stops the console filling up with 403s on the Help and Reports pages.
  let portalUser = false;
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const role = (JSON.parse(window.localStorage.getItem('user') || '{}') || {}).role;
      portalUser = !!role && role !== 'admin' && role !== 'super_admin' && role !== 'user';
    }
  } catch (e) {
    portalUser = false;
  }

  const endpoints = portalUser ? ['/settings/public'] : ['/settings', '/settings/public'];

  for (const path of endpoints) {
    try {
      const response = await fetch(`${API_URL}${path}`);
      if (!response.ok) continue;
      const data = await response.json();
      if (data && Array.isArray(data.raw)) {
        const flat = {};
        data.raw.forEach((row) => {
          // Ignore empty/null values so they never clobber the defaults
          // (e.g. an empty company_logo in settings must not hide the logo).
          if (row && row.key && row.value !== '' && row.value != null) flat[row.key] = row.value;
        });
        cached = { ...DEFAULT_COMPANY, ...flat };
        cachedAt = Date.now();
        return cached;
      }
    } catch (error) {
      // try the next endpoint
    }
  }

  return DEFAULT_COMPANY;
}

/** One Purpose bank details for payment blocks on PDFs. */
export async function getBankDetails() {
  const company = await getCompanyDetails();
  return {
    accountName: company.bank_account_name || DEFAULT_COMPANY.bank_account_name,
    bank: company.bank_name || DEFAULT_COMPANY.bank_name,
    accountNumber: company.bank_account_number || DEFAULT_COMPANY.bank_account_number,
    accountType: company.bank_account_type || DEFAULT_COMPANY.bank_account_type,
    branchCode: company.bank_branch_code || DEFAULT_COMPANY.bank_branch_code,
  };
}

/** Formatted one-line address (used by footers). */
export async function getFullAddress() {
  const company = await getCompanyDetails();
  return [company.company_address, company.company_address_line2].filter(Boolean).join(', ');
}

/** Clear the cached copy (call after the Company Profile is updated). */
export function clearCompanyCache() {
  cached = null;
  cachedAt = 0;
}

// ---- shape mappers -------------------------------------------------------

/** InvoicePDF component expects {name, address, phone, email, regNumber, vatNumber, logo}. */
export function toInvoiceShape(c) {
  return {
    name: c.company_name || DEFAULT_COMPANY.company_name,
    address: [c.company_address, c.company_address_line2].filter(Boolean).join(', '),
    phone: c.company_phone,
    email: c.company_email,
    regNumber: c.company_reg_number,
    vatNumber: c.company_vat_number,
    logo: c.company_logo,
    website: c.company_website,
  };
}

/** CreditAppPDF component expects {name, reg, vat, addr, tel, email, logo}. */
export function toCreditAppShape(c) {
  return {
    name: c.company_name || DEFAULT_COMPANY.company_name,
    reg: c.company_reg_number,
    vat: c.company_vat_number,
    addr: [c.company_address, c.company_address_line2].filter(Boolean).join(', '),
    tel: c.company_phone,
    email: c.company_email,
    logo: c.company_logo,
  };
}

export default {
  DEFAULT_COMPANY,
  getCompanyDetails,
  getBankDetails,
  getFullAddress,
  clearCompanyCache,
  toInvoiceShape,
  toCreditAppShape,
};
